/**
 * Zustand store for managing terminal tab state.
 * Handles multiple terminal tabs with connection states and split panels.
 *
 * @module components/terminal/useTerminalStore
 */

import { create } from 'zustand';
import type { ConnectionState } from '@/hooks';
import type { TerminalTab, SplitPanel } from './types';
import { MAX_TERMINAL_TABS } from './types';

/** Generate a unique ID for a new tab or panel */
function generateId(prefix: string = 'term'): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

interface TerminalStore {
  /** All terminal tabs */
  tabs: TerminalTab[];
  /** Currently active (focused) tab ID */
  activeTabId: string | null;
  /** Registry of terminal content getter functions */
  terminalContentGetters: Map<string, () => string>;

  // Tab actions
  /** Add a new terminal tab, returns the new tab ID */
  addTab: (shellName?: string) => string | null;
  /** Remove a tab by ID */
  removeTab: (tabId: string) => void;
  /** Set the active tab */
  setActiveTab: (tabId: string) => void;
  /** Update a tab's connection state */
  updateTabConnectionState: (tabId: string, state: ConnectionState) => void;
  /** Update a tab's session ID */
  updateTabSessionId: (tabId: string, sessionId: string | null) => void;

  // Split actions
  /** Toggle split panel for a tab - creates or removes the split */
  toggleSplit: (tabId: string) => void;
  /** Update split panel's connection state */
  updateSplitConnectionState: (tabId: string, state: ConnectionState) => void;
  /** Update split panel's session ID */
  updateSplitSessionId: (tabId: string, sessionId: string | null) => void;

  // Content getter actions
  /** Register a terminal content getter */
  registerTerminalContentGetter: (tabId: string, panelType: string, getter: () => string) => void;
  /** Unregister a terminal content getter */
  unregisterTerminalContentGetter: (tabId: string, panelType: string) => void;
  /** Get content from active terminal (main panel) */
  getActiveTerminalContent: () => string;

  // Helpers
  /** Check if we can add more tabs */
  canAddTab: () => boolean;
  /** Get a tab by ID */
  getTab: (tabId: string) => TerminalTab | undefined;
  /** Reset the store (used when unmounting) */
  reset: () => void;
}

/** Calculate the next instance number for a shell type */
function getNextInstanceNumber(tabs: TerminalTab[], shellName: string): number {
  const shellTabs = tabs.filter((t) => t.shellName === shellName);
  if (shellTabs.length === 0) return 1;

  // Find the highest instance number and add 1
  const maxInstance = Math.max(...shellTabs.map((t) => t.instanceNumber));
  return maxInstance + 1;
}

/** Create the initial tab */
function createInitialTab(): TerminalTab {
  const id = generateId();
  return {
    id,
    name: 'bash (1)',
    shellName: 'bash',
    instanceNumber: 1,
    connectionState: 'disconnected',
    sessionId: null,
    split: null,
  };
}

/** Create a new split panel */
function createSplitPanel(): SplitPanel {
  return {
    id: generateId('split'),
    connectionState: 'disconnected',
    sessionId: null,
  };
}

export type TerminalStoreApi = ReturnType<typeof createTerminalStore>;

/** Create an isolated terminal store instance */
export function createTerminalStore() {
  return create<TerminalStore>((set, get) => ({
    tabs: [createInitialTab()],
    activeTabId: null, // Will be set to first tab on init
    terminalContentGetters: new Map(),

    addTab: (shellName = 'bash') => {
      const { tabs } = get();

      if (tabs.length >= MAX_TERMINAL_TABS) {
        console.warn(`[TerminalStore] Maximum tabs (${MAX_TERMINAL_TABS}) reached`);
        return null;
      }

      const id = generateId();
      const instanceNumber = getNextInstanceNumber(tabs, shellName);
      const newTab: TerminalTab = {
        id,
        name: `${shellName} (${instanceNumber})`,
        shellName,
        instanceNumber,
        connectionState: 'disconnected',
        sessionId: null,
        split: null,
      };

      set((state) => ({
        tabs: [...state.tabs, newTab],
        activeTabId: id, // Auto-select new tab
      }));

      return id;
    },

    removeTab: (tabId: string) => {
      const { tabs, activeTabId } = get();

      // Don't allow removing the last tab
      if (tabs.length <= 1) {
        console.warn('[TerminalStore] Cannot remove the last tab');
        return;
      }

      const tabIndex = tabs.findIndex((t) => t.id === tabId);
      if (tabIndex === -1) return;

      const newTabs = tabs.filter((t) => t.id !== tabId);

      // Calculate new active tab if we're removing the active one
      let newActiveTabId = activeTabId;
      if (activeTabId === tabId) {
        // Select adjacent tab (prefer right, then left)
        if (tabIndex < newTabs.length) {
          newActiveTabId = newTabs[tabIndex].id;
        } else {
          newActiveTabId = newTabs[newTabs.length - 1].id;
        }
      }

      set({
        tabs: newTabs,
        activeTabId: newActiveTabId,
      });
    },

    setActiveTab: (tabId: string) => {
      const { tabs } = get();
      if (tabs.some((t) => t.id === tabId)) {
        set({ activeTabId: tabId });
      }
    },

    updateTabConnectionState: (tabId: string, state: ConnectionState) => {
      set((prev) => ({
        tabs: prev.tabs.map((t) => (t.id === tabId ? { ...t, connectionState: state } : t)),
      }));
    },

    updateTabSessionId: (tabId: string, sessionId: string | null) => {
      set((prev) => ({
        tabs: prev.tabs.map((t) => (t.id === tabId ? { ...t, sessionId } : t)),
      }));
    },

    toggleSplit: (tabId: string) => {
      const { tabs } = get();
      const tab = tabs.find((t) => t.id === tabId);
      if (!tab) return;

      if (tab.split) {
        // Remove split
        console.log(`[TerminalStore] Removing split from tab ${tabId}`);
        set((prev) => ({
          tabs: prev.tabs.map((t) => (t.id === tabId ? { ...t, split: null } : t)),
        }));
      } else {
        // Create split
        console.log(`[TerminalStore] Creating split for tab ${tabId}`);
        const newSplit = createSplitPanel();
        set((prev) => ({
          tabs: prev.tabs.map((t) => (t.id === tabId ? { ...t, split: newSplit } : t)),
        }));
      }
    },

    updateSplitConnectionState: (tabId: string, state: ConnectionState) => {
      set((prev) => ({
        tabs: prev.tabs.map((t) => {
          if (t.id === tabId && t.split) {
            return { ...t, split: { ...t.split, connectionState: state } };
          }
          return t;
        }),
      }));
    },

    updateSplitSessionId: (tabId: string, sessionId: string | null) => {
      set((prev) => ({
        tabs: prev.tabs.map((t) => {
          if (t.id === tabId && t.split) {
            return { ...t, split: { ...t.split, sessionId } };
          }
          return t;
        }),
      }));
    },

    registerTerminalContentGetter: (tabId: string, panelType: string, getter: () => string) => {
      const key = `${tabId}-${panelType}`;
      get().terminalContentGetters.set(key, getter);
    },

    unregisterTerminalContentGetter: (tabId: string, panelType: string) => {
      const key = `${tabId}-${panelType}`;
      get().terminalContentGetters.delete(key);
    },

    getActiveTerminalContent: () => {
      const { activeTabId, terminalContentGetters } = get();
      if (!activeTabId) return '';
      // Get main panel content
      const key = `${activeTabId}-main`;
      const getter = terminalContentGetters.get(key);
      return getter ? getter() : '';
    },

    canAddTab: () => {
      return get().tabs.length < MAX_TERMINAL_TABS;
    },

    getTab: (tabId: string) => {
      return get().tabs.find((t) => t.id === tabId);
    },

    reset: () => {
      const initialTab = createInitialTab();
      set({
        tabs: [initialTab],
        activeTabId: initialTab.id,
      });
    },
  }));
}

// --- React context for scoped stores ---
import { createContext, useContext } from 'react';
import { useStore } from 'zustand';

export const TerminalStoreContext = createContext<TerminalStoreApi | null>(null);

/**
 * Hook to access the nearest terminal store from context.
 * Falls back to a default singleton for backward compatibility.
 */
const defaultStore = createTerminalStore();

export function useTerminalStore<T>(selector: (state: TerminalStore) => T): T {
  const contextStore = useContext(TerminalStoreContext);
  const store = contextStore ?? defaultStore;
  return useStore(store, selector);
}
